import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import './CardEditor.css';
import AvatarUpload from './AvatarUpload';

// 获取当天日期字符串
function getTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function CardEditor({ template, showDate = true, showAuthor = true, showCount = true, showAvatar = true, customBgType, customBgColor, customBgGradient, customBgImage, cardWidth = 420, contentBgOpacity = 18, fontFamily = '', textColor = '#ffffff', textScale = 1, onSaveTemplate, avatar, onAvatarChange, initialContent }) {
  const [date, setDate] = useState(getTodayStr());
  const [author, setAuthor] = useState('未来已到');
  const [wordCount, setWordCount] = useState(0);
  const [copyNotice, setCopyNotice] = useState(null);
  
  const cardRef = useRef(null);
  const wrapperRef = useRef(null);
  const previewRef = useRef(null);
  const contentRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [manualScale, setManualScale] = useState(1);
  const noticeTimerRef = useRef(null);
  
  const defaultContent = initialContent || '讲一个笑话：说社会上最危险的两群人相遇了，带着红领巾的小明同学，扶起了摔倒了老奶奶。老奶奶拉着小明说，是你把我撞倒的，要赔我钱。小明反手就掐住了老奶奶的脖子说，老太婆，我今年9岁了，如果我跟他们说，你想拐卖我，并且要伤害我，我在情急之下还了手，结果把你掐死了，你觉得我能判几年？';

  // 初始化内容
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerText = defaultContent;
      updateWordCount();
    }
  }, [defaultContent]);

  useEffect(() => {
    const updateScale = () => {
      if (!wrapperRef.current) return;
      const available = wrapperRef.current.clientWidth;
      if (!available) return;
      const next = Math.min(1, (available - 24) / cardWidth);
      setPreviewScale(Number.isFinite(next) ? Math.max(0.6, next) : 1);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [cardWidth]);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);
  
  // 更新字数统计函数
  const updateWordCount = () => {
    if (contentRef.current) {
      const text = contentRef.current.innerText;
      const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
      setWordCount(chineseChars);
    }
  };

  // 处理输入事件，只更新字数
  const handleInput = (e) => {
    updateWordCount();
  };

  // 处理粘贴事件，清除格式
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    updateWordCount();
  };

  const showCopyNotice = (message) => {
    setCopyNotice(message);
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }
    noticeTimerRef.current = setTimeout(() => {
      setCopyNotice(null);
      noticeTimerRef.current = null;
    }, 1600);
  };

  const ensureFontsReady = async () => {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    if (fontFamily && document.fonts?.load) {
      try {
        await document.fonts.load(`16px ${fontFamily}`, '字体加载');
      } catch (error) {
        console.warn('Font load failed:', error);
      }
    }
  };

  const ensureCloneFontsReady = async (doc) => {
    const head = doc.head || doc.querySelector('head');
    if (!head) return;
    const fontLinks = Array.from(window.document.querySelectorAll('link[rel="stylesheet"]'))
      .filter((link) => link.href && link.href.includes('fonts.googleapis.com'));
    const loadPromises = fontLinks.map((link) => {
      const cloneLink = doc.createElement('link');
      cloneLink.rel = 'stylesheet';
      cloneLink.href = link.href;
      return new Promise((resolve) => {
        cloneLink.onload = () => resolve();
        cloneLink.onerror = () => resolve();
        head.appendChild(cloneLink);
      });
    });
    if (loadPromises.length) {
      await Promise.all(loadPromises);
    }
    if (doc.fonts?.ready) {
      await doc.fonts.ready;
    }
    if (fontFamily && doc.fonts?.load) {
      try {
        await doc.fonts.load(`16px ${fontFamily}`, '字体加载');
      } catch (error) {
        console.warn('Clone font load failed:', error);
      }
    }
  };

  const getExportBackgroundColor = () => {
    if (typeof window === 'undefined' || !window.getComputedStyle) {
      return '#181c24';
    }
    const bodyBg = window.getComputedStyle(document.body).backgroundColor;
    return bodyBg && bodyBg !== 'transparent' ? bodyBg : '#181c24';
  };

  // 计算自定义背景样式
  const getCustomStyle = () => {
    if (customBgType === 'color') {
      return { background: customBgColor };
    } else if (customBgType === 'gradient') {
      return { background: `linear-gradient(${customBgGradient.angle}deg, ${customBgGradient.from} 0%, ${customBgGradient.to} 100%)` };
    } else if (customBgType === 'image') {
      return {
        backgroundImage: `url(${customBgImage || `${process.env.PUBLIC_URL}/backgrounds/001.png`})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return {};
  };

  // 导出图片
  const handleExport = async () => {
    if (!cardRef.current) return;
    
    try {
      await ensureFontsReady();
      const canvas = await html2canvas(cardRef.current, { 
        backgroundColor: getExportBackgroundColor(), 
        scale: 2,
        logging: false,
        useCORS: true,
        onclone: async (doc) => {
          await ensureCloneFontsReady(doc);
          const clonedPreview = doc.querySelector('[data-card-preview="true"]');
          if (clonedPreview) {
            clonedPreview.style.transform = 'scale(1)';
          }
          const clonedCard = doc.querySelector('[data-card-capture="true"]');
          if (!clonedCard) return;
          if (fontFamily) {
            clonedCard.style.setProperty('font-family', fontFamily, 'important');
            clonedCard.querySelectorAll('*').forEach((el) => {
              el.style.setProperty('font-family', fontFamily, 'important');
            });
          }
          clonedCard.style.height = 'auto';
          clonedCard.style.overflow = 'visible';
        }
      });
      
      const link = document.createElement('a');
      link.download = 'card.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  // 复制图片
  const handleCopy = async () => {
    if (!cardRef.current) return;
    
    try {
      await ensureFontsReady();
      const canvas = await html2canvas(cardRef.current, { 
        backgroundColor: getExportBackgroundColor(), 
        scale: 2,
        logging: false,
        useCORS: true,
        onclone: async (doc) => {
          await ensureCloneFontsReady(doc);
          const clonedPreview = doc.querySelector('[data-card-preview="true"]');
          if (clonedPreview) {
            clonedPreview.style.transform = 'scale(1)';
          }
          const clonedCard = doc.querySelector('[data-card-capture="true"]');
          if (!clonedCard) return;
          if (fontFamily) {
            clonedCard.style.setProperty('font-family', fontFamily, 'important');
            clonedCard.querySelectorAll('*').forEach((el) => {
              el.style.setProperty('font-family', fontFamily, 'important');
            });
          }
          clonedCard.style.height = 'auto';
          clonedCard.style.overflow = 'visible';
        }
      });
      
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          showCopyNotice('图片已复制到剪贴板！');
        } catch (e) {
          showCopyNotice('复制失败，请使用导出功能');
        }
      });
    } catch (error) {
      console.error('Copy error:', error);
    }
  };
  
  // 获取卡片内当前内容 - 用于保存模板
  const getCurrentContent = () => {
    return contentRef.current ? contentRef.current.innerText : '';
  };

  // 保存模板时调用
  const handleSaveTemplate = () => {
    if (onSaveTemplate) {
      const currentContent = getCurrentContent();
      onSaveTemplate(currentContent);
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      paddingTop: '10px',
      textAlign: 'center'
    }}>
      {copyNotice && (
        <div className="copy-toast" role="status" aria-live="polite">
          {copyNotice}
        </div>
      )}
      <div className="card-editor-toolbar">
        <button className="card-btn" onClick={handleExport}>导出图片</button>
        <button className="card-btn" onClick={handleCopy}>复制图片</button>
        {onSaveTemplate && (
          <button className="card-btn" onClick={handleSaveTemplate}>保存为模板</button>
        )}
        <button
          type="button"
          className="card-btn"
          onClick={() => setManualScale((prev) => Math.max(0.6, Number((prev - 0.1).toFixed(1))))}
        >
          缩小
        </button>
        <button
          type="button"
          className="card-btn"
          onClick={() => setManualScale((prev) => Math.min(1.8, Number((prev + 0.1).toFixed(1))))}
        >
          放大
        </button>
      </div>
      <div ref={wrapperRef} style={{
        display:'block', 
        marginTop: '20px', 
        paddingBottom: '40px',
        textAlign: 'center',
        width: '100%'
      }}>
        <div
          ref={previewRef}
          data-card-preview="true"
          style={{
            transform: `scale(${previewScale * manualScale})`,
            transformOrigin: 'top center',
            display: 'inline-block'
          }}
        >
          <div
            ref={cardRef}
            data-card-capture="true"
            className={`cyber-card card-${template} ${customBgType === 'image' ? 'card-bg-zoom' : ''}`}
            style={{
              ...(customBgType !== 'template' ? getCustomStyle() : {}),
              width: cardWidth,
              fontFamily: fontFamily || undefined,
              color: textColor || undefined
            }}
          >
            <div
              className="card-inner"
              style={{
                background: customBgType === 'image' ? 'transparent' : `rgba(0,0,0,${contentBgOpacity / 100})`
              }}
            >
              {showAvatar && (
                <AvatarUpload 
                  avatar={avatar}
                  onAvatarChange={onAvatarChange}
                />
              )}
              {showDate && (
                <div 
                  className="cyber-date" 
                  contentEditable 
                  suppressContentEditableWarning 
                  onBlur={e => setDate(e.target.innerText)}
                  style={{ color: textColor, fontSize: `${18 * textScale}px` }}
                >
                  {date}
                </div>
              )}
              <div
                ref={contentRef}
                className="cyber-content"
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                style={{ color: textColor, fontSize: `${22 * textScale}px` }}
              ></div>
              <div className="cyber-footer">
                {showAuthor && (
                  <div className="cyber-footer-left">
                    <input
                      type="text"
                      className="cyber-author-input"
                      value={author}
                      onChange={e => setAuthor(e.target.value)}
                      placeholder="输入作者名"
                      style={{ color: textColor, fontSize: `${16 * textScale}px` }}
                    />
                  </div>
                )}
                {showCount && (
                  <span className="cyber-count" style={{ color: textColor, fontSize: `${17 * textScale}px` }}>字数: {wordCount}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
